import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, Loader2, ExternalLink } from "lucide-react";
import { API, formatCount } from "@/lib/config";
import { useCountry } from "@/components/contexts/CountryContext";

/**
 * The chart as it stood on a past day.
 *
 * Served entirely from stored snapshots, so it costs no YouTube quota and keeps
 * working long after those videos leave the live chart. Days only appear once the
 * snapshot job has been running - a fresh install starts empty and fills up daily.
 */
export const ArchivePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { country } = useCountry();

  const categoryId = searchParams.get("category_id") || "0";
  const selectedDate = searchParams.get("date") || "";

  const [dates, setDates] = useState([]);
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(`${API}/archive/dates`, {
          params: { country: country.code, category_id: categoryId, limit: 60 },
        });
        if (cancelled) return;
        const available = res.data.dates || [];
        setDates(available);
        if (!selectedDate && available.length) {
          setSearchParams(
            { date: available[0].date, category_id: categoryId },
            { replace: true }
          );
        }
      } catch {
        if (!cancelled) setDates([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country.code, categoryId]);

  useEffect(() => {
    if (!selectedDate) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/archive/chart`, {
          params: { date: selectedDate, country: country.code, category_id: categoryId },
        });
        if (!cancelled) setChart(res.data);
      } catch {
        if (!cancelled) setChart(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedDate, country.code, categoryId]);

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="archive-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <CalendarDays className="w-6 h-6 text-orange-500" />
          <h1 className="font-heading text-2xl sm:text-3xl font-black">Trending Archive</h1>
        </div>
        <p className="text-sm text-white/50 mb-6">
          What was trending in {country.name} on a given day.
        </p>

        {dates.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-sm text-white/50">
            No archived days yet. Snapshots are captured hourly — check back tomorrow.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-8 max-h-28 overflow-y-auto">
              {dates.map((d) => (
                <button
                  key={d.date}
                  onClick={() => setSearchParams({ date: d.date, category_id: categoryId }, { replace: true })}
                  className={`px-2.5 py-1 min-h-[44px] sm:min-h-0 rounded-lg text-xs font-medium transition-colors ${
                    selectedDate === d.date
                      ? "bg-orange-500 text-black"
                      : "bg-white/5 border border-white/10 hover:bg-white/10"
                  }`}
                >
                  {d.date}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex justify-center py-24">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              </div>
            ) : !chart?.entries?.length ? (
              <p className="text-center text-sm text-white/40 py-16">No snapshot stored for this day.</p>
            ) : (
              <ol className="space-y-2">
                {chart.entries.map((entry) => (
                  <li
                    key={entry.video_id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10"
                  >
                    <span className="shrink-0 w-8 text-center font-heading font-black text-white/30">
                      {entry.rank}
                    </span>
                    <img
                      src={entry.thumbnail_url}
                      alt=""
                      loading="lazy"
                      className="shrink-0 w-24 sm:w-32 aspect-video object-cover rounded-lg bg-white/5"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-bold line-clamp-2">{entry.title}</p>
                      <p className="text-[11px] text-white/40 truncate">{entry.channel_title}</p>
                      <p className="text-[11px] text-white/30 mt-0.5">
                        {formatCount(entry.view_count_raw)} views at capture
                      </p>
                    </div>
                    <a
                      href={entry.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 flex w-11 h-11 items-center justify-center rounded-lg hover:bg-white/10 text-white/50 hover:text-orange-400 transition-colors"
                      aria-label="Open on YouTube"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </div>
    </div>
  );
};
